import { apiAdminClient } from '@/services/api-client'
import type {
  AuthUser,
  ChangePasswordRequest,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  LoginRequest,
  LogoutRequest,
  RefreshRequest,
  ResetPasswordRequest,
  SessionResponse,
} from '@/features/auth/types/auth.types'

// A API responde em snake_case (ver auth.schema.ts de api-navega-backoffice-nodejs).
// Estes tipos representam o payload bruto da rede antes da conversão para camelCase.
interface SessionResponseDto {
  access_token: string
  refresh_token: string
  expires_in: number
  must_change_password: boolean
}

interface AuthUserDto {
  id: string
  name: string
  email: string
  role: string
  permissions: string[]
  must_change_password: boolean
}

function toSessionResponse(dto: SessionResponseDto): SessionResponse {
  return {
    accessToken: dto.access_token,
    refreshToken: dto.refresh_token,
    expiresIn: dto.expires_in,
    mustChangePassword: dto.must_change_password,
  }
}

function toAuthUser(dto: AuthUserDto): AuthUser {
  return {
    id: dto.id,
    name: dto.name,
    email: dto.email,
    role: dto.role,
    permissions: dto.permissions,
    mustChangePassword: dto.must_change_password,
  }
}

export const authService = {
  login: (data: LoginRequest) =>
    apiAdminClient
      .post<SessionResponseDto>('/auth/login', { email: data.email, password: data.password })
      .then(toSessionResponse),

  // `skipAuthRefresh: true` evita que o próprio endpoint de refresh dispare
  // uma nova tentativa de refresh sobre si mesmo em caso de 401 (loop
  // infinito) — ver `src/services/auth-refresh.ts`, que é quem chama este
  // método e trata esse 401 como fatal.
  refresh: (data: RefreshRequest) =>
    apiAdminClient
      .post<SessionResponseDto>(
        '/auth/refresh',
        { refresh_token: data.refreshToken },
        { skipAuthRefresh: true },
      )
      .then(toSessionResponse),

  logout: (data: LogoutRequest, token: string) =>
    apiAdminClient.post<void>('/auth/logout', { refresh_token: data.refreshToken }, { token }),

  logoutAll: (token: string) => apiAdminClient.post<void>('/auth/logout-all', undefined, { token }),

  forgotPassword: (data: ForgotPasswordRequest) =>
    apiAdminClient.post<ForgotPasswordResponse>('/auth/forgot-password', { email: data.email }),

  resetPassword: (data: ResetPasswordRequest) =>
    apiAdminClient.post<void>('/auth/reset-password', {
      token: data.token,
      new_password: data.newPassword,
    }),

  changePassword: (data: ChangePasswordRequest, token: string) =>
    apiAdminClient.post<void>(
      '/auth/change-password',
      { current_password: data.currentPassword, new_password: data.newPassword },
      { token },
    ),

  me: (token: string) => apiAdminClient.get<AuthUserDto>('/auth/me', { token }).then(toAuthUser),
}
